import { useSelector } from "react-redux";

import {
  DegradedBanner,
  InvoiceStripSlot,
  LinkBtn,
  MetaText,
  PaymentPill,
  SentBanner,
  WarningBanner,
  WarningBody,
  WarningLabel,
} from "../FeeNoticeQueueStyled";
import { describePaymentStatus, formatMoney } from "../utils";

/**
 * What this letter owes the client, pinned where the reviewer cannot
 * miss it (Matthew, 2026-08-28: "The agent has to click over to the
 * 'cost invoice' tab to know anything at all about a cost invoice").
 *
 * Confirming is terminal — it writes the fee to Salesforce and the
 * letter leaves the queue — so a reviewer who never opened the invoice
 * tab could file a letter away with the firm's advanced costs unbilled
 * and nothing recording that they were owed. The server refuses that
 * confirm, but a refusal at the last keystroke is a wall; this is the
 * part that tells them at the start.
 *
 * Renders on both tabs, so state stays visible while working the
 * invoice itself.
 */

const sentDetail = (lastSend, sendCount) => {
  if (!lastSend) return "";
  const parts = [];
  if (lastSend.to) parts.push(`to ${lastSend.to}`);
  if (lastSend.at) {
    const when = new Date(lastSend.at);
    if (!Number.isNaN(when.getTime())) {
      parts.push(
        `on ${when.toLocaleDateString()} at ${when.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })}`
      );
    }
  }
  if (lastSend.by_email) parts.push(`by ${lastSend.by_email}`);
  const line = parts.join(" ");
  return sendCount > 1 ? `${line} (${sendCount} sends total)` : line;
};

const CostInvoiceStrip = ({ onOpenInvoiceTab }) => {
  const { currentItem, invoice: payload } = useSelector(
    (state) => state.feeNotices
  );

  // No matter means no cost ledger to read and no invoice possible; the
  // invoice tab is disabled in that state too.
  if (!currentItem?.matter_id) return null;
  // Still loading, or the endpoint is switched off entirely. Saying
  // nothing is right: an empty slot reads as "nothing to do here", and
  // a spinner in a status strip is noise.
  if (!payload) return null;

  const invoice = payload.invoice || {};
  const total = invoice.total_costs;
  const invoiceNumber = invoice.invoice_number;
  const created = Boolean(payload.saved);
  const sendCount = payload.sends?.length || 0;
  const sent = sendCount > 0;
  const paymentView = describePaymentStatus(payload.payment_status);

  const open = onOpenInvoiceTab ? (
    <LinkBtn type="button" onClick={onOpenInvoiceTab}>
      Open the Cost Invoice tab
    </LinkBtn>
  ) : null;

  // Salesforce is the source of the cost ledger, so when it cannot be
  // reached we do not know whether anything is owed. Left silent, the
  // reviewer would read silence as "nothing owed" — and the one time
  // that is wrong, the costs are never billed. The confirm gate
  // deliberately stands down in this case rather than freezing the
  // queue, and stamps the letter so it can be found and billed later.
  if (payload.salesforce_error && !created) {
    return (
      <InvoiceStripSlot>
        <DegradedBanner>
          <strong>Cost invoice — couldn&rsquo;t check.</strong> Salesforce
          didn&rsquo;t answer, so we can&rsquo;t tell whether this client
          owes case costs. You can still confirm; the letter is flagged
          for someone to bill later. {open}
        </DegradedBanner>
      </InvoiceStripSlot>
    );
  }

  if (sent) {
    return (
      <InvoiceStripSlot>
        <SentBanner>
          <strong>
            Cost invoice sent
            {invoiceNumber ? ` — ${invoiceNumber}` : ""}
            {total ? ` for ${formatMoney(total)}` : ""}
          </strong>{" "}
          {sentDetail(payload.last_send, sendCount)}
          {paymentView ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginLeft: 6,
              }}
            >
              <PaymentPill tone={paymentView.tone}>
                {paymentView.label}
              </PaymentPill>
              {paymentView.detail && (
                <MetaText>{paymentView.detail}</MetaText>
              )}
            </span>
          ) : (
            " — no payment yet."
          )}
        </SentBanner>
      </InvoiceStripSlot>
    );
  }

  if (created) {
    // Quieter than the owed state on purpose: the reviewer has done
    // real work and is one press from finishing. But a live payment
    // link now exists in the world that no client has been given, so
    // say that plainly.
    return (
      <InvoiceStripSlot>
        <DegradedBanner>
          <strong>Cost invoice created — not sent.</strong>{" "}
          {invoiceNumber ? `${invoiceNumber} ` : ""}
          {total ? `for ${formatMoney(total)} ` : ""}
          is ready with a live payment link. The client has not received
          it, and Confirm stays locked until it goes out. {open}
        </DegradedBanner>
      </InvoiceStripSlot>
    );
  }

  const warrant = payload.warrant;
  if (warrant && warrant.warranted === false) {
    // The common case — most letters owe nothing. Deliberately quiet:
    // an amber bar on every letter is a bar nobody reads by the end of
    // the week. Extraction does miss awards, so the way in stays.
    return (
      <InvoiceStripSlot>
        <MetaText>
          No cost invoice needed
          {warrant.reason ? ` — ${warrant.reason}` : "."}{" "}
          {onOpenInvoiceTab && (
            <LinkBtn type="button" onClick={onOpenInvoiceTab}>
              Prepare one anyway
            </LinkBtn>
          )}
        </MetaText>
      </InvoiceStripSlot>
    );
  }

  return (
    <InvoiceStripSlot>
      <WarningBanner>
        <WarningLabel>
          Cost invoice owed{total ? ` — ${formatMoney(total)}` : ""}
        </WarningLabel>
        <WarningBody>
          Not created yet, so nothing has been sent and nothing can be
          paid. Confirm is locked until it goes out. {open}
        </WarningBody>
      </WarningBanner>
    </InvoiceStripSlot>
  );
};

export default CostInvoiceStrip;
